/**
 * Read-only service codes table. Default code/description come from the linked
 * ChargeItemDefinition; once billing changes, the ChargeItem is authoritative.
 * Units come from the ChargeItem and are not editable here.
 */
import { Group, Text } from '@mantine/core';
import { InlineFieldWarningIcon } from '@/components/InlineFieldWarningIcon';
import { UnifyCardTable } from '@/components/UnifyCardTable';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { COMPACT_TABLE_STYLES } from '@/components/unifyTableVariants';

export interface AppointmentServiceCodeRow {
  id: string;
  code: string;
  description: string;
  units: number;
}

export interface AppointmentServiceCodesTableProps {
  rows: AppointmentServiceCodeRow[];
  appearance?: 'inline' | 'card';
  warningByRowIndex?: Record<number, string>;
  withNoteFieldAnchors?: boolean;
}

type TableRow = AppointmentServiceCodeRow & { _index: number };

function buildColumns(
  warningByRowIndex: Record<number, string>,
  withNoteFieldAnchors: boolean,
): UnifyTableColumn<TableRow>[] {
  return [
    {
      key: 'code',
      label: 'Code',
      render: (row) => {
        const warning = warningByRowIndex[row._index];
        return (
          <Group
            gap={6}
            wrap="nowrap"
            {...(withNoteFieldAnchors
              ? { 'data-note-field': `billingRow-${row._index}` }
              : {})}
          >
            <Text size="sm" fw={600}>
              {row.code}
            </Text>
            {warning ? <InlineFieldWarningIcon message={warning} /> : null}
          </Group>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      render: (row) => (
        <Text size="sm" c="gray.8">
          {row.description || '—'}
        </Text>
      ),
    },
    {
      key: 'units',
      label: 'Units',
      align: 'right',
      render: (row) => (
        <Text size="sm" ta="right">
          {row.units}
        </Text>
      ),
    },
  ];
}

export function AppointmentServiceCodesTable({
  rows,
  appearance = 'inline',
  warningByRowIndex = {},
  withNoteFieldAnchors = false,
}: AppointmentServiceCodesTableProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No service codes
      </Text>
    );
  }

  const tableData = rows.map((row, _index) => ({ ...row, _index }));
  const columns = buildColumns(warningByRowIndex, withNoteFieldAnchors);

  if (appearance === 'card') {
    return (
      <UnifyCardTable
        data={tableData}
        columns={columns}
        getRowKey={(row) => `${row.id}-${row._index}`}
      />
    );
  }

  return (
    <UnifyTable
      data={tableData}
      columns={columns}
      getRowKey={(row) => `${row.id}-${row._index}`}
      withPaper={false}
      scrollMinWidth={0}
      tableProps={{
        fz: 'sm',
        w: '100%',
        styles: COMPACT_TABLE_STYLES,
      }}
    />
  );
}
